<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import ContentTemplateParser from 'dashboard/components-next/content-templates/ContentTemplateParser.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import SidebarActionsHeader from 'dashboard/components-next/SidebarActionsHeader.vue';

const emit = defineEmits(['close']);

const { t } = useI18n();
const store = useStore();
const { uiSettings, updateUISettings } = useUISettings();

const currentChat = useMapGetter('getSelectedChat');
const currentUser = useMapGetter('getCurrentUser');

const activeTab = ref('favoritos');
const selectedTemplate = ref(null);
const query = ref('');
const isRefreshing = ref(false);

const inboxId = computed(() => currentChat.value?.inbox_id);

const allTemplates = computed(() => {
  const inbox = store.getters['inboxes/getInbox'](inboxId.value);
  return (inbox?.content_templates?.templates || []).filter(
    tpl => tpl.status === 'approved'
  );
});

// ============================================================
// Secciones personalizadas DE LA BANDEJA de esta conversación: las pestañas
// del panel son "Favoritos" (fija) + las secciones definidas en Ajustes >
// Plantillas para esta bandeja, en su orden.
// ============================================================
const sections = computed(() =>
  inboxId.value
    ? store.getters['templateClassifications/getSections'](inboxId.value)
    : []
);
const hydratedTemplates = computed(() =>
  inboxId.value
    ? store.getters['templateClassifications/getTemplates'](inboxId.value)
    : []
);

const TABS = computed(() => [
  { key: 'favoritos', label: t('CONTENT_TEMPLATES.PANEL.FAVORITES_TAB') },
  ...sections.value.map(s => ({ key: `sec_${s.id}`, id: s.id, label: s.name })),
]);

const activeTabObject = computed(() =>
  TABS.value.find(tab => tab.key === activeTab.value)
);

const fetchInboxSections = () => {
  if (!inboxId.value) return;
  const loaded = store.getters['templateClassifications/isInboxLoaded'](
    inboxId.value
  );
  if (!loaded) {
    // eslint-disable-next-line no-console
    console.log(
      '[ContentTemplatesPanel] fetching sections for inbox',
      inboxId.value
    );
    store.dispatch('templateClassifications/get', { inboxId: inboxId.value });
  }
};

onMounted(fetchInboxSections);
watch(inboxId, fetchInboxSections);

// Si desaparece la sección activa (p. ej. la bandeja cambió), volver a Favoritos.
watch(TABS, tabs => {
  if (!tabs.some(tab => tab.key === activeTab.value)) {
    activeTab.value = 'favoritos';
  }
});

// Nombres ordenados de una sección, según el drag & drop del admin.
const namesForSection = sectionId =>
  hydratedTemplates.value
    .filter(tpl => tpl.section_id === sectionId)
    .sort((a, b) => (a.position || 0) - (b.position || 0))
    .map(tpl => tpl.name);

// Favorites stored in ui_settings as an array of content_sid strings
const favoriteIds = computed(() => uiSettings.value?.template_favorites || []);

const isFavorite = tpl => favoriteIds.value.includes(tpl.content_sid);

const toggleFavorite = tpl => {
  const current = favoriteIds.value;
  const next = isFavorite(tpl)
    ? current.filter(id => id !== tpl.content_sid)
    : [...current, tpl.content_sid];
  updateUISettings({ template_favorites: next });
};

// Alias de visualización { friendly_name => nombre personalizado } definido
// por el admin en la página Plantillas PARA ESTA BANDEJA; fallback al mapa
// legacy del usuario mientras la hidratación por bandeja no ha llegado.
const templateDisplayNames = computed(() => {
  const fromInbox = {};
  hydratedTemplates.value.forEach(tpl => {
    if (tpl.display_name) fromInbox[tpl.name] = tpl.display_name;
  });
  return Object.keys(fromInbox).length
    ? fromInbox
    : currentUser.value?.template_display_names || {};
});
const displayNameOf = tpl =>
  templateDisplayNames.value[tpl.friendly_name] || tpl.friendly_name;

// Sorts templates by their position in the ordered name list (admin's manual
// drag-and-drop order from the Plantillas page). Templates not present in the
// list keep their natural order at the end.
const orderByList = (templates, names) => {
  const idx = new Map(names.map((n, i) => [n, i]));
  return [...templates].sort(
    (a, b) =>
      (idx.has(a.friendly_name) ? idx.get(a.friendly_name) : Infinity) -
      (idx.has(b.friendly_name) ? idx.get(b.friendly_name) : Infinity)
  );
};

const templatesForTab = computed(() => {
  const q = query.value.toLowerCase();
  let list = [];

  if (activeTab.value === 'favoritos') {
    list = allTemplates.value.filter(tpl => isFavorite(tpl));
  } else if (activeTabObject.value?.id) {
    const names = namesForSection(activeTabObject.value.id);
    const set = new Set(names);
    list = set.size
      ? orderByList(
          allTemplates.value.filter(tpl => set.has(tpl.friendly_name)),
          names
        )
      : [];
  }

  if (!q) return list;
  return list.filter(
    tpl =>
      tpl.friendly_name.toLowerCase().includes(q) ||
      displayNameOf(tpl).toLowerCase().includes(q)
  );
});

// Sección sin plantillas asignadas todavía (se ordenan en Ajustes > Plantillas).
const isEmptySectionTab = computed(() => {
  const tab = activeTabObject.value;
  if (!tab?.id) return false;
  return namesForSection(tab.id).length === 0;
});

const selectTab = tab => {
  activeTab.value = tab;
  selectedTemplate.value = null;
  query.value = '';
};

const selectTemplate = template => {
  selectedTemplate.value = template;
};

const resetTemplate = () => {
  selectedTemplate.value = null;
};

const refreshTemplates = async () => {
  if (!inboxId.value) return;
  isRefreshing.value = true;
  try {
    await store.dispatch('inboxes/syncTemplates', inboxId.value);
    useAlert(t('CONTENT_TEMPLATES.PICKER.REFRESH_SUCCESS'));
  } catch {
    useAlert(t('CONTENT_TEMPLATES.PICKER.REFRESH_ERROR'));
  } finally {
    isRefreshing.value = false;
  }
};

const sendTemplate = async payload => {
  if (!currentChat.value?.id) return;
  try {
    await store.dispatch('createPendingMessageAndSend', {
      conversationId: currentChat.value.id,
      ...payload,
    });
    emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
    selectedTemplate.value = null;
  } catch (error) {
    const errorMessage =
      error?.response?.data?.error || t('CONVERSATION.MESSAGE_ERROR');
    useAlert(errorMessage);
  }
};

const closePanel = () => emit('close');
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-hidden">
    <SidebarActionsHeader
      :title="$t('CONVERSATION.SIDEBAR.TEMPLATES')"
      @close="closePanel"
    />

    <!-- Tabs -->
    <div class="flex border-b border-n-weak px-2 pt-1 gap-0.5 flex-shrink-0">
      <button
        v-for="tab in TABS"
        :key="tab.key"
        class="px-3 py-2 text-xs font-medium rounded-t-lg transition-colors"
        :class="
          activeTab === tab.key
            ? 'text-n-brand border-b-2 border-n-brand bg-n-alpha-1'
            : 'text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-1'
        "
        @click="selectTab(tab.key)"
      >
        {{ tab.label }}
      </button>
    </div>

    <!-- Template detail view -->
    <div
      v-if="selectedTemplate"
      class="flex flex-col flex-1 overflow-auto p-3 gap-3"
    >
      <button
        class="flex items-center gap-1 text-xs text-n-slate-11 hover:text-n-slate-12"
        @click="resetTemplate"
      >
        <Icon icon="i-ph-arrow-left" class="size-3.5" />
        {{ $t('CONTENT_TEMPLATES.PARSER.GO_BACK_LABEL') }}
      </button>

      <ContentTemplateParser
        :template="selectedTemplate"
        @reset-template="resetTemplate"
        @send-message="sendTemplate"
      >
        <template #actions="{ sendMessage, disabled }">
          <Button
            :label="$t('CONTENT_TEMPLATES.PARSER.SEND_MESSAGE_LABEL')"
            class="w-full mt-4"
            :disabled="disabled"
            @click="sendMessage"
          />
        </template>
      </ContentTemplateParser>
    </div>

    <!-- Template list view -->
    <div v-else class="flex flex-col flex-1 overflow-hidden">
      <!-- Search + Refresh -->
      <div class="flex gap-2 px-3 pt-3 pb-2 flex-shrink-0">
        <div
          class="flex flex-1 gap-1 items-center px-2.5 py-0 rounded-lg bg-n-alpha-black2 outline outline-1 outline-n-weak hover:outline-n-slate-6 focus-within:outline-n-brand"
        >
          <Icon
            icon="i-lucide-search"
            class="text-n-slate-12 size-4 flex-shrink-0"
          />
          <input
            v-model="query"
            type="search"
            :placeholder="$t('CONTENT_TEMPLATES.PICKER.SEARCH_PLACEHOLDER')"
            class="reset-base w-full h-8 bg-transparent text-n-slate-12 !text-sm !outline-0"
          />
        </div>
        <button
          :disabled="isRefreshing"
          class="flex justify-center items-center w-8 h-8 rounded-lg bg-n-alpha-black2 outline outline-1 outline-n-weak hover:outline-n-slate-6 hover:bg-n-alpha-2 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
          :title="$t('CONTENT_TEMPLATES.PICKER.REFRESH_BUTTON')"
          @click="refreshTemplates"
        >
          <Icon
            icon="i-lucide-refresh-ccw"
            class="text-n-slate-12 size-3.5"
            :class="{ 'animate-spin': isRefreshing }"
          />
        </button>
      </div>

      <!-- Empty favorites notice -->
      <div
        v-if="activeTab === 'favoritos' && templatesForTab.length === 0"
        class="flex flex-col items-center justify-center flex-1 px-4 text-center gap-3"
      >
        <Icon icon="i-ph-star" class="size-8 text-n-slate-9" />
        <p class="text-sm text-n-slate-11">
          {{ $t('CONTENT_TEMPLATES.PANEL.EMPTY_FAVORITES_TITLE') }}
        </p>
        <p class="text-xs text-n-slate-9">
          {{ $t('CONTENT_TEMPLATES.PANEL.EMPTY_FAVORITES_HINT') }}
        </p>
      </div>

      <!-- Empty section notice -->
      <div
        v-else-if="isEmptySectionTab"
        class="flex flex-col items-center justify-center flex-1 px-4 text-center gap-3"
      >
        <Icon icon="i-ph-list-dashes" class="size-8 text-n-slate-9" />
        <p class="text-sm text-n-slate-11">
          {{ $t('CONTENT_TEMPLATES.PANEL.EMPTY_SECTION_TITLE') }}
        </p>
        <p class="text-xs text-n-slate-9">
          {{ $t('CONTENT_TEMPLATES.PANEL.EMPTY_SECTION_HINT') }}
        </p>
      </div>

      <!-- Template list -->
      <div v-else class="flex-1 overflow-y-auto px-2 pb-3">
        <div v-if="templatesForTab.length">
          <div
            v-for="(template, i) in templatesForTab"
            :key="template.content_sid"
            class="group"
          >
            <div
              class="flex items-start gap-2 px-2 py-2.5 rounded-lg hover:bg-n-alpha-2 transition-colors"
            >
              <!-- Star button -->
              <button
                class="flex-shrink-0 mt-0.5 transition-colors"
                :class="
                  isFavorite(template)
                    ? 'text-y-600 hover:text-y-700 dark:text-y-400 dark:hover:text-y-300'
                    : 'text-n-slate-10 hover:text-y-600 dark:text-n-slate-10 dark:hover:text-y-400'
                "
                :title="
                  isFavorite(template)
                    ? $t('CONTENT_TEMPLATES.PANEL.REMOVE_FAVORITE')
                    : $t('CONTENT_TEMPLATES.PANEL.ADD_FAVORITE')
                "
                @click="toggleFavorite(template)"
              >
                <Icon
                  :icon="isFavorite(template) ? 'i-ph-star-fill' : 'i-ph-star'"
                  class="size-4"
                />
              </button>
              <!-- Template info -->
              <button
                class="flex-1 text-left min-w-0"
                @click="selectTemplate(template)"
              >
                <div class="flex items-start gap-2 mb-1">
                  <p
                    class="text-sm font-medium text-n-slate-12 leading-snug flex-1 min-w-0 break-words"
                  >
                    {{ displayNameOf(template) }}
                  </p>
                  <span
                    class="inline-block px-1.5 py-0.5 text-[10px] leading-none rounded bg-n-slate-3 text-n-slate-11 whitespace-nowrap flex-shrink-0"
                  >
                    {{ template.language }}
                  </span>
                </div>
                <p class="text-xs text-n-slate-11 line-clamp-2">
                  {{
                    template.body || $t('CONTENT_TEMPLATES.PICKER.NO_CONTENT')
                  }}
                </p>
              </button>
            </div>
            <hr
              v-if="i !== templatesForTab.length - 1"
              class="border-b border-n-weak mx-2"
            />
          </div>
        </div>
        <div
          v-else
          class="flex flex-col items-center justify-center py-12 text-center gap-2"
        >
          <Icon icon="i-ph-magnifying-glass" class="size-7 text-n-slate-9" />
          <p class="text-sm text-n-slate-11">
            {{
              query
                ? $t('CONTENT_TEMPLATES.PICKER.NO_TEMPLATES_FOUND')
                : $t('CONTENT_TEMPLATES.PICKER.NO_TEMPLATES_AVAILABLE')
            }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>
